/**
 * RestorativeInput Component
 * ===========================
 * Input panel for restorative data (crowns, fillings, caries, treatment needs).
 * 
 * Save this file to: frontend/src/components/charting/RestorativeInput.tsx
 */

import { useState, useEffect } from 'react';
import { useChartStore, useSelectedToothData, useIsChartEditable } from '@/stores/chartStore';
import { cn } from '@/lib/utils';
import type {
  RestorativeData,
  CrownData,
  FillingData,
  CariesData,
  TreatmentNeededData,
  ToothSurface,
  CrownMaterial,
  FillingMaterial,
  RestorationCondition,
  CariesSeverity,
  TreatmentProcedure,
  TreatmentPriority,
} from '@/types';

interface RestorativeInputProps {
  toothNumber: number;
}

const SURFACES: ToothSurface[] = ['M', 'O', 'D', 'B', 'L'];
const CROWN_MATERIALS: { value: CrownMaterial; label: string }[] = [
  { value: 'pfm', label: 'PFM' },
  { value: 'ceramic', label: 'Ceramic' },
  { value: 'zirconia', label: 'Zirconia' },
  { value: 'gold', label: 'Gold' },
  { value: 'emax', label: 'E-Max' },
  { value: 'other', label: 'Other' },
];

const FILLING_MATERIALS: { value: FillingMaterial; label: string }[] = [
  { value: 'amalgam', label: 'Amalgam' },
  { value: 'composite', label: 'Composite' },
  { value: 'gold', label: 'Gold' },
  { value: 'glass_ionomer', label: 'Glass Ionomer' },
  { value: 'other', label: 'Other' },
];

const CONDITIONS: { value: RestorationCondition; label: string }[] = [
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

const CARIES_SEVERITY: { value: CariesSeverity; label: string }[] = [
  { value: 'incipient', label: 'Incipient' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
];

const TREATMENT_PROCEDURES: { value: TreatmentProcedure; label: string }[] = [
  { value: 'crown', label: 'Crown' },
  { value: 'filling', label: 'Filling' },
  { value: 'rct', label: 'Root Canal' },
  { value: 'extraction', label: 'Extraction' },
  { value: 'srp', label: 'SRP' },
  { value: 'veneer', label: 'Veneer' },
  { value: 'implant', label: 'Implant' },
  { value: 'watch', label: 'Watch' },
  { value: 'refer', label: 'Refer' },
];

const TREATMENT_PRIORITIES: { value: TreatmentPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'soon', label: 'Soon' },
  { value: 'elective', label: 'Elective' },
];

export function RestorativeInput({ toothNumber }: RestorativeInputProps) {
  const toothData = useSelectedToothData();
  const isEditable = useIsChartEditable();
  const { updateToothData } = useChartStore();

  const [restorative, setRestorative] = useState<RestorativeData>(
    toothData?.restorative || {}
  );

  // Sync with store
  useEffect(() => {
    setRestorative(toothData?.restorative || {});
  }, [toothNumber, toothData?.restorative]);

  // Update store when local state changes
  const updateRestorative = (updates: Partial<RestorativeData>) => {
    const newRestorative = { ...restorative, ...updates };
    setRestorative(newRestorative);
    updateToothData(toothNumber, { restorative: newRestorative });
  };

  // Crown handlers
  const handleCrownToggle = () => {
    const currentCrown = restorative.crown || { present: false };
    updateRestorative({
      crown: { ...currentCrown, present: !currentCrown.present },
    });
  };

  const handleCrownMaterial = (material: CrownMaterial) => {
    updateRestorative({
      crown: { ...restorative.crown, present: true, material },
    });
  };

  const handleCrownCondition = (condition: RestorationCondition) => {
    updateRestorative({
      crown: { ...restorative.crown, present: true, condition },
    });
  };

  // Filling handlers
  const addFilling = () => {
    const fillings = restorative.fillings || [];
    updateRestorative({
      fillings: [...fillings, { surfaces: [], material: 'composite' }],
    });
  };

  const updateFilling = (index: number, updates: Partial<FillingData>) => {
    const fillings = [...(restorative.fillings || [])];
    fillings[index] = { ...fillings[index], ...updates };
    updateRestorative({ fillings });
  };

  const removeFilling = (index: number) => {
    const fillings = [...(restorative.fillings || [])];
    fillings.splice(index, 1);
    updateRestorative({ fillings });
  };

  const toggleFillingSurface = (index: number, surface: ToothSurface) => {
    const fillings = [...(restorative.fillings || [])];
    const filling = fillings[index];
    const surfaces = [...filling.surfaces];
    const surfaceIndex = surfaces.indexOf(surface);
    
    if (surfaceIndex >= 0) {
      surfaces.splice(surfaceIndex, 1);
    } else {
      surfaces.push(surface);
    }
    
    fillings[index] = { ...filling, surfaces };
    updateRestorative({ fillings });
  };

  // Caries handlers
  const handleCariesToggle = () => {
    const currentCaries = restorative.caries || { present: false };
    updateRestorative({
      caries: { ...currentCaries, present: !currentCaries.present },
    });
  };

  const toggleCariesSurface = (surface: ToothSurface) => {
    const caries = restorative.caries || { present: true, surfaces: [] };
    const surfaces = [...(caries.surfaces || [])];
    const index = surfaces.indexOf(surface);
    
    if (index >= 0) {
      surfaces.splice(index, 1);
    } else {
      surfaces.push(surface);
    }
    
    updateRestorative({ caries: { ...caries, present: true, surfaces } });
  };

  const handleCariesSeverity = (severity: CariesSeverity) => {
    updateRestorative({
      caries: { ...restorative.caries, present: true, severity },
    });
  };

  // Root canal handlers
  const handleRootCanalToggle = () => {
    const rct = restorative.root_canal || { treated: false };
    updateRestorative({
      root_canal: { ...rct, treated: !rct.treated },
    });
  };

  const handlePostToggle = () => {
    const rct = restorative.root_canal || { treated: true };
    updateRestorative({
      root_canal: { ...rct, treated: true, has_post: !rct.has_post },
    });
  };

  // Treatment handlers
  const handleTreatmentProcedure = (procedure: TreatmentProcedure | '') => {
    if (procedure === '') {
      updateRestorative({ treatment_needed: undefined });
    } else {
      updateRestorative({
        treatment_needed: {
          ...restorative.treatment_needed,
          procedure,
        } as TreatmentNeededData,
      });
    }
  };

  const handleTreatmentPriority = (priority: TreatmentPriority) => {
    updateRestorative({
      treatment_needed: {
        ...restorative.treatment_needed,
        procedure: restorative.treatment_needed?.procedure || 'crown',
        priority,
      } as TreatmentNeededData,
    });
  };

  const handleTreatmentNotes = (notes: string) => {
    updateRestorative({
      treatment_needed: {
        ...restorative.treatment_needed,
        procedure: restorative.treatment_needed?.procedure || 'crown',
        notes,
      } as TreatmentNeededData,
    });
  };

  return (
    <div className="space-y-4">
      {/* Crown Section */}
      <div className="border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Crown</h4>
          <button
            onClick={handleCrownToggle}
            disabled={!isEditable}
            className={cn(
              'px-3 py-1 text-xs rounded-full border transition-colors',
              restorative.crown?.present
                ? 'bg-yellow-100 border-yellow-300 text-yellow-700'
                : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
            )}
          >
            {restorative.crown?.present ? 'Has Crown' : 'No Crown'}
          </button>
        </div>
        
        {restorative.crown?.present && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Material</label>
              <select
                value={restorative.crown?.material || ''}
                onChange={(e) => handleCrownMaterial(e.target.value as CrownMaterial)}
                disabled={!isEditable}
                className="w-full text-sm border rounded px-2 py-1"
              >
                <option value="">Select...</option>
                {CROWN_MATERIALS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Condition</label>
              <select
                value={restorative.crown?.condition || ''}
                onChange={(e) => handleCrownCondition(e.target.value as RestorationCondition)}
                disabled={!isEditable}
                className="w-full text-sm border rounded px-2 py-1"
              >
                <option value="">Select...</option>
                {CONDITIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Fillings Section */}
      <div className="border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Fillings</h4>
          <button
            onClick={addFilling}
            disabled={!isEditable}
            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
          >
            + Add Filling
          </button>
        </div>
        
        {(restorative.fillings || []).map((filling, index) => (
          <div key={index} className="border-t pt-2 mt-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Filling {index + 1}</span>
              <button
                onClick={() => removeFilling(index)}
                disabled={!isEditable}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
            
            <div className="mb-2">
              <label className="block text-xs text-gray-500 mb-1">Surfaces</label>
              <div className="flex gap-1">
                {SURFACES.map((surface) => (
                  <button
                    key={surface}
                    onClick={() => toggleFillingSurface(index, surface)}
                    disabled={!isEditable}
                    className={cn(
                      'w-8 h-8 text-xs rounded border transition-colors',
                      filling.surfaces.includes(surface)
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                    )}
                  >
                    {surface}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Material</label>
                <select
                  value={filling.material}
                  onChange={(e) => updateFilling(index, { material: e.target.value as FillingMaterial })}
                  disabled={!isEditable}
                  className="w-full text-sm border rounded px-2 py-1"
                >
                  {FILLING_MATERIALS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Condition</label>
                <select
                  value={filling.condition || ''}
                  onChange={(e) => updateFilling(index, { condition: e.target.value as RestorationCondition })}
                  disabled={!isEditable}
                  className="w-full text-sm border rounded px-2 py-1"
                >
                  <option value="">Select...</option>
                  {CONDITIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
        
        {(restorative.fillings || []).length === 0 && (
          <p className="text-xs text-gray-400 mt-2">No fillings recorded</p>
        )}
      </div>

      {/* Caries Section */}
      <div className="border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Caries</h4>
          <button
            onClick={handleCariesToggle}
            disabled={!isEditable}
            className={cn(
              'px-3 py-1 text-xs rounded-full border transition-colors',
              restorative.caries?.present
                ? 'bg-red-100 border-red-300 text-red-700'
                : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
            )}
          >
            {restorative.caries?.present ? 'Has Caries' : 'No Caries'}
          </button>
        </div>
        
        {restorative.caries?.present && (
          <>
            <div className="mb-2">
              <label className="block text-xs text-gray-500 mb-1">Surfaces</label>
              <div className="flex gap-1">
                {SURFACES.map((surface) => (
                  <button
                    key={surface}
                    onClick={() => toggleCariesSurface(surface)}
                    disabled={!isEditable}
                    className={cn(
                      'w-8 h-8 text-xs rounded border transition-colors',
                      (restorative.caries?.surfaces || []).includes(surface)
                        ? 'bg-red-100 border-red-300 text-red-700'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                    )}
                  >
                    {surface}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-gray-500 mb-1">Severity</label>
              <div className="flex gap-2">
                {CARIES_SEVERITY.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => handleCariesSeverity(s.value)}
                    disabled={!isEditable}
                    className={cn(
                      'px-2 py-1 text-xs rounded border transition-colors',
                      restorative.caries?.severity === s.value
                        ? 'bg-red-100 border-red-300 text-red-700'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Root Canal Section */}
      <div className="border rounded-lg p-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Root Canal</h4>
          <div className="flex gap-2">
            <button
              onClick={handleRootCanalToggle}
              disabled={!isEditable}
              className={cn(
                'px-3 py-1 text-xs rounded-full border transition-colors',
                restorative.root_canal?.treated
                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              )}
            >
              {restorative.root_canal?.treated ? 'RCT Done' : 'No RCT'}
            </button>
            {restorative.root_canal?.treated && (
              <button
                onClick={handlePostToggle}
                disabled={!isEditable}
                className={cn(
                  'px-3 py-1 text-xs rounded-full border transition-colors',
                  restorative.root_canal?.has_post
                    ? 'bg-purple-100 border-purple-300 text-purple-700'
                    : 'bg-gray-50 border-gray-200 text-gray-500'
                )}
              >
                {restorative.root_canal?.has_post ? 'Has Post' : 'No Post'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Treatment Needed Section */}
      <div className="border rounded-lg p-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Treatment Needed</h4>
        
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Procedure</label>
            <select
              value={restorative.treatment_needed?.procedure || ''}
              onChange={(e) => handleTreatmentProcedure(e.target.value as TreatmentProcedure | '')}
              disabled={!isEditable}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">None</option>
              {TREATMENT_PROCEDURES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          
          {restorative.treatment_needed?.procedure && (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Priority</label>
                <div className="flex gap-2">
                  {TREATMENT_PRIORITIES.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => handleTreatmentPriority(p.value)}
                      disabled={!isEditable}
                      className={cn(
                        'px-2 py-1 text-xs rounded border transition-colors',
                        restorative.treatment_needed?.priority === p.value
                          ? p.value === 'urgent'
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : p.value === 'soon'
                            ? 'bg-orange-100 border-orange-300 text-orange-700'
                            : 'bg-green-100 border-green-300 text-green-700'
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text"
                  value={restorative.treatment_needed?.notes || ''}
                  onChange={(e) => handleTreatmentNotes(e.target.value)}
                  disabled={!isEditable}
                  placeholder="Additional notes..."
                  className="w-full text-sm border rounded px-2 py-1"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
